import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import analytics from "@/lib/analytics";
import { logger } from "@/lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      analytics.identify(data);
    } catch (err) {
      // 401 on first load with no token is expected; only log unexpected errors.
      if (err?.response && err.response.status !== 401) {
        logger.warn("AuthContext.checkAuth unexpected error:", err.response.status, err.response.data);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth (session_id in hash), let AuthCallback handle it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password, totp_code) => {
    const { data } = await api.post("/auth/login", { email, password, totp_code });
    if (data.requires_2fa) return { requires_2fa: true };
    // Access token is now delivered as an HttpOnly cookie (`vqms_access_token`)
    // by the backend — no longer persisted to localStorage. Any pre-existing
    // localStorage token from before the cutover is proactively removed.
    localStorage.removeItem("vq_token");
    setUser(data.user);
    analytics.identify(data.user);
    analytics.track("user_logged_in", { plan: data.user?.plan });
    return { user: data.user };
  }, []);

  const register = useCallback(async (email, password, name, beta_code) => {
    const { data } = await api.post("/auth/register", { email, password, name, beta_code });
    localStorage.removeItem("vq_token");
    setUser(data.user);
    analytics.identify(data.user);
    analytics.track("user_signed_up", { plan: data.user?.plan });
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Server-side logout is best-effort; local session still cleared below.
      logger.warn("AuthContext.logout server call failed:", err?.response?.status);
    }
    // Cookie clear happens server-side; also clear any lingering legacy key.
    localStorage.removeItem("vq_token");
    setUser(null);
    analytics.reset();
  }, []);

  // Split value memo: user + loading + refresh (setter-like) are the frequent-change deps;
  // login/register/logout are stable useCallbacks captured once.
  const authActions = useMemo(
    () => ({ login, register, logout, refresh: checkAuth, setUser }),
    [login, register, logout, checkAuth],
  );
  const value = useMemo(
    () => ({ user, loading, ...authActions }),
    [user, loading, authActions],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
